'use client'
import { type FC, type ReactNode, createContext, useState, useCallback, useRef } from 'react'
import { useParams } from 'next/navigation'
import { Toast, type iToastProps, ToastWrapper } from '@/components'
import { LOCALE } from '@/utils'
import type { TypeLang } from '@/schema'

interface iToastState extends iToastProps {
  id: number;
}

export interface ToastContextProps {
  addToast: (toastData: iToastProps) => void;
}

type Props = {
  children: ReactNode
}

export const ToastContext = createContext<ToastContextProps | undefined>(undefined)

export const ToastProvider: FC<Props> = ({ children }) => {
  const { lang }: { lang: TypeLang } = useParams()
  const [toasts, setToasts] = useState<iToastState[]>([])
  const toastIdRef = useRef(0)

  const addToast = useCallback((toastData: iToastProps) => {
    setToasts((prevToasts) => [
      ...prevToasts,
      { ...toastData, id: toastIdRef.current++ },
    ])
  }, [setToasts])

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <ToastWrapper aria-live='polite' aria-atomic='true' aria-label={NOTIFICATIONS_TRANSLATIONS[lang ?? LOCALE as TypeLang]}>
        {Array.isArray(toasts) ? toasts.map((toast) => (
          <Toast
            key={toast.id}
            {...toast}
            {...typeof toast.message !== 'string' && { message: 'Unexpected error' }}
            onClose={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
          />
        )):
          <Toast
            key={'fallback_toast'}
            message='Unexpected error'
            type='error'
          />
        }
      </ToastWrapper>
    </ToastContext.Provider>
  )
}

const NOTIFICATIONS_TRANSLATIONS = {
  en: 'Notifications',
  es: 'Notificaciones',
  ca: 'Notificacions',
  fr: 'Notifications',
  pt: 'Notificações',
  de: 'Benachrichtigungen',
  it: 'Notifiche',
  pl: 'Powiadomienia',
  ru: 'Уведомления',
  ja: '通知',
  zh: '通知',
  ko: '알림',
  nl: 'Meldingen',
  da: 'Notifikationer',
  sv: 'Aviseringar',
  no: 'Varsler',
  fi: 'Ilmoitukset',
  tr: 'Bildirimler',
  el: 'Ειδοποιήσεις',
  cs: 'Oznámení',
  hu: 'Értesítések',
  ar: 'الإشعارات'
} as const
